import React, { useEffect, useRef, useState } from 'react';
import { devError } from '../lib/devLog';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PriceTag from '../components/PriceTag';
import { ArrowRight, Camera, MapPin, Quote, BookOpen, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SHIRLEY_ID = 'shirley-baker-de573754';   // internal artist id (used by artworks.artistId)
const SHIRLEY_SLUG = 'shirley-baker';           // clean URL slug

// Display order — narrative arc: pioneering colour → the B&W social documents
// she's most cited for → her Camden punks turn → dogs (the wry comic relief)
// → her seaside pictures (Blackpool then the South of France).
const SERIES_ORDER = [
  {
    key: 'Street Photographs — 1965 Colour',
    label: '1965 · Colour',
    kicker: 'Kodachrome · Hulme & Manchester',
    intro:
      "Shirley shot Kodachrome at a time when serious British photographers wouldn't touch colour. These pictures — children swinging from lampposts, mothers gathered around an ice-cream van — are now her most reproduced and the cornerstone of her recent reappraisal.",
  },
  {
    key: 'Street Photographs — 1960s B&W',
    label: '1960s · B&W',
    kicker: 'Salford & Manchester · Slum Clearances',
    intro:
      "The body of work she's most celebrated for. Between 1961 and 1981 Shirley walked the streets of Salford and Manchester with a Rolleiflex round her neck as whole neighbourhoods were demolished around her — and quietly built one of the great social documents of post-war Britain.",
  },
  {
    key: 'Street Photographs — 1960s',
    label: '1960s · Mixed',
    kicker: 'Working-class North England',
    intro: 'Further pictures from the same decade — found moments, the wry humour Shirley was known for, the ordinary lives the planners forgot.',
  },
  {
    key: 'Punks',
    label: 'Punks · 1980s',
    kicker: 'Camden Lock & Camden Town',
    intro:
      "By the early '80s Shirley was photographing punks in and around Camden Lock — the same unposed, eye-level register she'd taken to Salford, brought to a new subculture growing on a London street.",
  },
  {
    key: 'Dogs',
    label: 'Dogs',
    kicker: 'A lifelong sideline · Coalmen, dog shows, strays',
    intro:
      "Dogs ran through Shirley's work the whole way through — a coalman and his dog, the local dog show, strays trotting past the rubble. She loved them, and her dog pictures carry a comic, slightly mournful warmth that runs counter to the more serious frames.",
  },
  {
    key: 'On the Beach — Blackpool',
    label: 'Blackpool',
    kicker: '1970s · The British seaside',
    intro:
      "When Shirley wasn't on the streets of Manchester she was at Blackpool — balloon men, smoking mums, dudes asleep on the sand. Pictures full of warmth, sunburn and quiet British holiday absurdity.",
  },
  {
    key: 'On the Beach — South of France',
    label: 'South of France',
    kicker: '1970s · Sun-worshippers, Mediterranean',
    intro:
      "Her Mediterranean counterpoint — Côte d'Azur sun-worshippers and couples on the sand, shot with the same unposed, sideways eye she brought to Manchester.",
  },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const Section = ({ children, className = '', id = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ArtworkCard = ({ work, navigate, index }) => {
  const src = work.imageUrl?.startsWith('http') ? work.imageUrl : `${API_URL}${work.imageUrl}`;
  return (
    <motion.button
      type="button"
      onClick={() => navigate(`/artwork/${work.slug || work.id}`)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.32) }}
      whileHover={{ y: -4 }}
      className="group text-left w-full"
      data-testid={`sb-artwork-card-${work.id}`}
    >
      <div className="aspect-[4/5] mb-4 bg-neutral-100 overflow-hidden">
        <img
          src={src}
          alt={work.altText || `${work.title} by Shirley Baker`}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
        />
      </div>
      <h3 className="text-base font-medium tracking-tight leading-snug line-clamp-2 group-hover:underline underline-offset-4 decoration-1">
        {work.title}
      </h3>
      <div className="mt-1 flex items-baseline justify-between gap-3 text-sm text-neutral-500">
        <span className="truncate">{work.year || ''}</span>
        <span className="font-medium text-black whitespace-nowrap">
          <PriceTag priceDisplay={work.priceDisplay} vatScheme={work.vatScheme || 'standard'} />
        </span>
      </div>
    </motion.button>
  );
};

const SeriesSection = ({ id, label, kicker, intro, works, navigate }) => {
  if (!works || !works.length) return null;
  return (
    <Section id={id} className="py-20 sm:py-28 border-t border-neutral-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">{kicker}</div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">{label}</h2>
          <p className="mt-5 text-neutral-600 leading-relaxed text-base sm:text-lg">{intro}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {works.map((w, i) => (
            <ArtworkCard key={w.id} work={w} navigate={navigate} index={i} />
          ))}
        </div>
      </div>
    </Section>
  );
};

// Sticky chip nav — same UX pattern as Marilyn / SBM pages so the catalogue
// stays navigable without breaking the editorial reading order.
const SeriesNav = ({ available }) => {
  const [activeKey, setActiveKey] = useState(available[0]?.key);
  const stripRef = useRef(null);
  const programmaticUntilRef = useRef(0);

  useEffect(() => {
    if (!available.length) return undefined;
    const observers = available.map((g) => {
      const el = document.getElementById(`sb-series-${slugify(g.key)}`);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          if (Date.now() < programmaticUntilRef.current) return;
          setActiveKey(g.key);
        },
        { rootMargin: '-40% 0px -55% 0px', threshold: 0 },
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available.length]);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const chip = strip.querySelector(`[data-sb-series-key="${activeKey}"]`);
    if (!chip) return;
    const target = chip.offsetLeft - (strip.clientWidth / 2) + (chip.clientWidth / 2);
    strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [activeKey]);

  const handleJump = (key) => {
    const el = document.getElementById(`sb-series-${slugify(key)}`);
    if (!el) return;
    programmaticUntilRef.current = Date.now() + 1200;
    setActiveKey(key);
    const offset = window.innerWidth >= 1024 ? 180 : 120;
    const y = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  if (!available.length) return null;

  return (
    <div
      className="sticky top-[64px] lg:top-[124px] z-30 bg-white/95 backdrop-blur border-y border-neutral-200"
      data-testid="sb-series-nav"
    >
      <div
        ref={stripRef}
        className="max-w-7xl mx-auto flex gap-1 sm:gap-2 overflow-x-auto px-4 sm:px-6 py-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {available.map((g) => {
          const isActive = g.key === activeKey;
          return (
            <button
              key={g.key}
              data-sb-series-key={g.key}
              data-testid={`sb-nav-chip-${slugify(g.key)}`}
              type="button"
              onClick={() => handleJump(g.key)}
              className={`shrink-0 px-3 sm:px-4 py-1.5 text-xs sm:text-[13px] tracking-wide whitespace-nowrap transition-colors duration-200 border ${
                isActive
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-neutral-700 border-neutral-200 hover:border-black hover:text-black'
              }`}
            >
              {g.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ShirleyBakerPage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [artist, setArtist] = useState(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchAll = async () => {
      try {
        const [aRes, wRes] = await Promise.all([
          fetch(`${API_URL}/api/artists/${SHIRLEY_ID}`),
          fetch(`${API_URL}/api/artworks?artistId=${SHIRLEY_ID}&limit=500`),
        ]);
        if (aRes.ok) setArtist(await aRes.json());
        if (wRes.ok) {
          const data = await wRes.json();
          // Stable display order: available first, then by year ascending, then title.
          const sorted = [...data].sort((a, b) => {
            const aAvail = a.available === true ? 0 : 1;
            const bAvail = b.available === true ? 0 : 1;
            if (aAvail !== bAvail) return aAvail - bAvail;
            return (a.title || '').localeCompare(b.title || '');
          });
          setWorks(sorted);
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') devError('Shirley fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    // Mount-only fetch — module-level constants (API_URL, SHIRLEY_ID).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group works by exact series string, mapped into our curated narrative order.
  const grouped = works.reduce((acc, w) => {
    const k = w.series || 'Other';
    (acc[k] = acc[k] || []).push(w);
    return acc;
  }, {});

  const availableSeries = SERIES_ORDER.filter((s) => (grouped[s.key] || []).length > 0);

  // Pick a cinematic hero — prefer the iconic 1965 Hulme colour shots, fall
  // back to whatever's first.
  const heroPick =
    works.find((w) => /ice cream van|lamppost|hulme/i.test(w.title || '')) ||
    works.find((w) => w.series === 'Street Photographs — 1965 Colour') ||
    works[0];
  const heroSrc = heroPick?.imageUrl?.startsWith('http')
    ? heroPick.imageUrl
    : heroPick?.imageUrl
      ? `${API_URL}${heroPick.imageUrl}`
      : null;

  return (
    <div className="min-h-screen bg-white" data-testid="shirley-baker-page">
      <SEO
        title="Shirley Baker Photographs | Zebra One Gallery"
        description="Shirley Baker (1932–2014). Pioneering British social-documentary photographer of post-war Manchester and Salford, Camden punks and the British seaside. View the full 24-print collection at Zebra One Gallery, Hampstead."
        canonical={typeof window !== 'undefined' ? window.location.origin + '/artist/' + SHIRLEY_SLUG : undefined}
        image={heroSrc || undefined}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: 'Shirley Baker',
          birthDate: '1932',
          deathDate: '2014',
          nationality: 'British',
          jobTitle: 'Photographer',
          description:
            "British social-documentary photographer best known for her unposed black-and-white and pioneering colour pictures of Salford and Manchester during the slum-clearance programmes of 1961–1981.",
          ...(heroSrc && { image: heroSrc }),
        }}
      />
      <Header />

      {/* ─────────────────────────── Hero ─────────────────────────── */}
      <motion.section
        ref={heroRef}
        style={{ opacity, scale }}
        className="relative min-h-[88vh] flex items-end overflow-hidden border-b border-neutral-200 bg-black"
        data-testid="sb-hero"
      >
        {heroSrc && (
          <div className="absolute inset-0">
            <img
              src={heroSrc}
              alt="Shirley Baker — Manchester, 1965"
              className="w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40" />
          </div>
        )}

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 pb-16 sm:pb-24 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="max-w-3xl space-y-6 text-white"
          >
            <div className="inline-block px-3 py-1.5 border border-white/40 text-[11px] tracking-[0.3em] uppercase backdrop-blur-sm bg-white/10">
              Social Documentary · 1932 – 2014
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.95]">
              Shirley<br /><span className="italic">Baker</span>.
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/85 max-w-2xl font-light leading-relaxed">
              The Rolleiflex round her neck, Salford and Manchester being demolished around her — and one of post-war Britain's great social documents being quietly built.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => document.getElementById('sb-collection')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-black hover:bg-white/90 transition-colors text-sm font-medium tracking-wider"
                data-testid="sb-hero-browse-cta"
              >
                <Camera className="w-4 h-4" />
                Browse the Collection
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => document.getElementById('sb-story')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/60 text-white hover:bg-white hover:text-black transition-colors text-sm font-medium tracking-wider backdrop-blur-sm"
                data-testid="sb-hero-story-cta"
              >
                <BookOpen className="w-4 h-4" />
                Her Story
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* ─────────────────────────── Strap-line stats ─────────────────────────── */}
      <Section className="py-10 sm:py-14 bg-neutral-950 text-white border-b border-neutral-900">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-center md:text-left">
          <div className="flex md:items-start gap-4 flex-col md:flex-row items-center">
            <MapPin className="w-6 h-6 mt-1 text-white/60 shrink-0" />
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-1">Born</p>
              <p className="text-lg leading-snug">Kersal, North Salford · 1932</p>
            </div>
          </div>
          <div className="flex md:items-start gap-4 flex-col md:flex-row items-center">
            <Camera className="w-6 h-6 mt-1 text-white/60 shrink-0" />
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-1">Streetwork</p>
              <p className="text-lg leading-snug">1961 – 1981 · Salford &amp; Manchester</p>
            </div>
          </div>
          <div className="flex md:items-start gap-4 flex-col md:flex-row items-center">
            <BookOpen className="w-6 h-6 mt-1 text-white/60 shrink-0" />
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-1">Collected by</p>
              <p className="text-lg leading-snug">Tate · Manchester Art Gallery · The Photographers' Gallery</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── Story ─────────────────────────── */}
      <Section id="sb-story" className="py-24 sm:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">A Life Through the Rolleiflex</div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-10">
            One of the very few women in post-war Britain to be given formal photographic training.
          </h2>

          <div className="prose prose-neutral prose-lg max-w-none space-y-6 text-neutral-700 leading-relaxed">
            <p>
              Shirley Baker (1932–2014) was born in Kersal, North Salford. She studied Pure
              Photography at <strong>Manchester College of Technology</strong>, with further training
              at Regent Street Polytechnic and the London College of Printing, and — at sixty —
              completed an MA in Critical History and Theory of Photography at the University of
              Derby.
            </p>
            <p>
              Between <strong>1961 and 1981</strong> Shirley turned her Rolleiflex on the working-class
              inner-city streets of Salford and Manchester as they were torn apart by the
              slum-clearance programmes. Working unposed and on the move, she produced an
              empathetic, often wryly humorous body of work in both black-and-white and —
              remarkably for the period — pioneering <em>Kodachrome</em> colour.
            </p>
            <p>
              Her subjects were the women, children and loitering men whose communities the
              planners forgot: kids swinging from lampposts, mothers chatting over an ice-cream
              van, washing strung across the rubble. In the 1980s she also photographed punks in
              and around Camden Lock, the interiors of the Royal Manchester Children's Hospital
              and the daily rhythms of Manchester Airport.
            </p>
            <p>
              Her work has been published by <strong>Bloodaxe</strong>, <strong>The Lowry</strong>,
              The Photographers' Gallery, <strong>MACK</strong>, <strong>Hoxton Mini Press</strong> and
              Café Royal, and exhibited at The Photographers' Gallery (2015 — one of their most
              highly-attended shows), Manchester Art Gallery, <strong>Tate Britain</strong>
              {' '}(<em>Fear and Freedom</em>, 2025–), the Barbican (<em>Postwar Modern</em>, 2022)
              and the Sainsbury Centre. Her photographs are now held in major public collections in
              the UK.
            </p>
          </div>

          {/* Pull quote */}
          <blockquote className="not-prose border-l-2 border-black pl-6 my-16">
            <Quote className="w-6 h-6 mb-4 text-neutral-400" />
            <p className="text-2xl sm:text-3xl font-light leading-snug text-neutral-900 italic">
              "I love the immediacy of unposed, spontaneous photographs and the ability of the
              camera to capture the serious, the funny, the sublime and the ridiculous."
            </p>
            <footer className="text-sm text-neutral-500 mt-4 not-italic">— Shirley Baker</footer>
          </blockquote>
        </div>
      </Section>

      {/* ─────────────────────────── Three-panel pillars ─────────────────────────── */}
      <Section className="py-20 sm:py-28 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="border-l-2 border-black pl-6">
              <p className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">The streets</p>
              <h3 className="text-2xl font-light tracking-tight mb-3">Salford &amp; Manchester</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Twenty years on the same pavements as the bulldozers — recording the
                neighbourhoods that the planners erased.
              </p>
            </div>
            <div className="border-l-2 border-black pl-6">
              <p className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">The colour</p>
              <h3 className="text-2xl font-light tracking-tight mb-3">Pioneering Kodachrome</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                She used colour film in the 1960s when serious British photographers refused to.
                These pictures look more contemporary every year.
              </p>
            </div>
            <div className="border-l-2 border-black pl-6">
              <p className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">The legacy</p>
              <h3 className="text-2xl font-light tracking-tight mb-3">A late rediscovery</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                The 2015 Photographers' Gallery show — one of their most-visited ever — put her
                where she always belonged: alongside the great post-war British documentarians.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── Press & Exhibitions Timeline ─────────────────────────── */}
      <Section className="py-24 sm:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-16">
            <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">Press &amp; Exhibitions</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">
              Forty years of recognition — quiet at first, then everywhere.
            </h2>
            <p className="mt-5 text-neutral-600 leading-relaxed">
              Shirley worked for two decades on the streets of Salford and Manchester before her
              first major book; another two before the major institutional shows. Selected milestones below.
            </p>
          </div>

          <ol className="relative border-l border-neutral-200 ml-3 sm:ml-6 space-y-12">
            {[
              {
                year: '1989',
                title: 'Street Photographs · Bloodaxe Books',
                body: "Her first major monograph — the Salford and Manchester pictures she'd quietly been making since the early '60s, finally published in book form.",
              },
              {
                year: '2000',
                title: 'Streets and Spaces · The Lowry, Salford',
                body: "The Lowry mounts her first major institutional retrospective, reuniting her colour and black-and-white work for a Salford audience that recognised every street.",
              },
              {
                year: '2015',
                title: 'Women, Children and Loitering Men · The Photographers\u2019 Gallery, London',
                body: 'One of the most highly-attended shows in The Photographers\u2019 Gallery\u2019s history. The single moment that put Baker back in the national conversation about post-war British photography.',
              },
              {
                year: '2019',
                title: 'Shirley Baker · MACK Books',
                body: 'MACK\u2019s definitive monograph collects the colour and B&W work alongside Anna Douglas\u2019s essay — now the standard reference on Baker.',
              },
              {
                year: '2022',
                title: 'Postwar Modern: New Art in Britain 1945\u201365 · Barbican Art Gallery',
                body: 'Baker shown alongside Bacon, Bomberg, Auerbach and Hepworth in the Barbican\u2019s major postwar British survey — quiet vindication of where her work belongs.',
              },
              {
                year: '2024',
                title: 'Hoxton Mini Press · Manchester Streets Pocket Edition',
                body: 'A pocket-edition reissue of the Manchester pictures — the kind of cult, hand-sold photo-book that introduces a whole new generation of readers to Baker.',
              },
              {
                year: '2025\u2013',
                title: 'Fear and Freedom: British Photography 1945\u20131989 · Tate Britain',
                body: 'Tate Britain hangs Baker in a major British photography survey, the canonical institutional confirmation.',
              },
              {
                year: '2026',
                title: 'Shirley Baker · Zebra One Gallery, Hampstead',
                body: 'A summer exhibition of estate-authorised prints, with an evening hosted by Nan Levy \u2014 Shirley\u2019s daughter and keeper of the archive \u2014 marking what would have been Baker\u2019s 94th birthday.',
              },
            ].map((ev) => (
              <li key={ev.year + ev.title} className="ml-6 sm:ml-8 relative" data-testid={`sb-timeline-${ev.year}`}>
                <span className="absolute -left-[33px] sm:-left-[41px] top-1.5 w-3 h-3 bg-white border border-black rounded-full" />
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-8">
                  <div className="shrink-0 text-sm tracking-widest text-neutral-500 sm:w-24 sm:pt-0.5">{ev.year}</div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-medium tracking-tight mb-2">{ev.title}</h3>
                    <p className="text-sm sm:text-base text-neutral-600 leading-relaxed max-w-2xl">{ev.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-16 pt-10 border-t border-neutral-200 text-xs uppercase tracking-[0.3em] text-neutral-400">
            Public collections include — Tate · Manchester Art Gallery · The Photographers&rsquo; Gallery · Sainsbury Centre · Salford Museum
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── An Evening with Nan Levy ─────────────────────────── */}
      <Section className="py-20 sm:py-28 bg-neutral-950 text-white" data-testid="sb-nan-evening">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-4">Save the date · 25 June 2026</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-tight mb-6">
              An evening with <span className="italic">Nan Levy</span>.
            </h2>
            <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-4">
              Join us at the gallery for an intimate evening with Nan Levy — Shirley&rsquo;s daughter
              and keeper of her archive — marking what would have been Shirley&rsquo;s 94th birthday
              with the opening preview of our summer exhibition and an exclusive print run.
            </p>
            <ul className="text-sm sm:text-base text-white/70 space-y-1 mb-8 leading-relaxed">
              <li><span className="text-white/40 inline-block w-20">Date</span> Thursday, 25 June 2026</li>
              <li><span className="text-white/40 inline-block w-20">Time</span> 6:00 pm – 8:00 pm</li>
              <li><span className="text-white/40 inline-block w-20">Location</span> Zebra One Gallery · 1 Perrins Court, Hampstead NW3</li>
              <li><span className="text-white/40 inline-block w-20">RSVP</span> info@zebraonegallery.com</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:info@zebraonegallery.com?subject=An%20Evening%20with%20Nan%20Levy%20%E2%80%94%2025%20June%202026"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-black hover:bg-white/90 transition-colors text-sm font-medium tracking-wider"
                data-testid="sb-nan-rsvp-cta"
              >
                <Mail className="w-4 h-4" />
                RSVP for the evening
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => navigate('/news/news-shirley-baker-nan-levy-evening-2026')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/60 text-white hover:bg-white hover:text-black transition-colors text-sm font-medium tracking-wider"
                data-testid="sb-nan-read-more-cta"
              >
                <BookOpen className="w-4 h-4" />
                Read the announcement
              </button>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <figure className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_creative-showcase-578/artifacts/cdlbjkjn_fadee1b3-7427-4f1c-aab9-2b640ee16013.jpeg"
                alt="Nan Levy at Zebra One Gallery, with two of her mother's 1960s Manchester pictures"
                loading="lazy"
                className="w-full h-auto object-cover ring-1 ring-white/10"
              />
              <figcaption className="text-xs text-white/40 mt-3 italic leading-relaxed">
                Nan Levy at the gallery, with two of her mother&rsquo;s 1960s Manchester pictures.
              </figcaption>
            </figure>
          </div>
        </div>
      </Section>

      {/* ─────────────────────────── Catalogue header ─────────────────────────── */}
      <div id="sb-collection" />
      {loading ? (
        <Section className="py-32 text-center text-neutral-500" data-testid="sb-loading">
          <div className="inline-block w-8 h-8 border-2 border-neutral-300 border-t-black rounded-full animate-spin" />
          <p className="mt-4 text-sm">Loading the collection…</p>
        </Section>
      ) : (
        <>
          <Section className="pt-24 sm:pt-28 pb-4">
            <div className="max-w-7xl mx-auto px-6">
              <div className="max-w-3xl">
                <div className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-3">The Collection</div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">
                  {works.length} photographs, one extraordinary archive.
                </h2>
                <p className="mt-5 text-neutral-600 leading-relaxed">
                  Every Shirley Baker print held at Zebra One Gallery, grouped into the bodies of
                  work she returned to throughout her life. Click any photograph to see edition
                  details and price.
                </p>
              </div>
            </div>
          </Section>

          <SeriesNav available={availableSeries} />

          {availableSeries.map((s) => (
            <SeriesSection
              key={s.key}
              id={`sb-series-${slugify(s.key)}`}
              label={s.label}
              kicker={s.kicker}
              intro={s.intro}
              works={grouped[s.key] || []}
              navigate={navigate}
            />
          ))}
        </>
      )}

      {/* ─────────────────────────── CTA ─────────────────────────── */}
      <Section className="py-24 sm:py-32 bg-black text-white border-t border-neutral-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Camera className="w-10 h-10 mx-auto mb-6 text-white/70" />
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-6">
            Enquire about a Shirley Baker print.
          </h2>
          <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto mb-10">
            All works are estate-authorised prints, available framed or unframed. Pricing is on
            request — get in touch with Gabrielle and the gallery team to discuss availability,
            framing options and shipping.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => navigate('/contact?subject=Shirley%20Baker%20Enquiry')}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-black hover:bg-neutral-100 transition-colors text-sm uppercase tracking-[0.2em] font-medium"
              data-testid="sb-cta-enquire"
            >
              <Mail className="w-4 h-4" />
              Make an Enquiry
              <ArrowRight className="w-5 h-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => navigate(`/shop?artistId=${SHIRLEY_ID}`)}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-white/60 text-white hover:bg-white hover:text-black transition-colors text-sm uppercase tracking-[0.2em] font-medium"
              data-testid="sb-cta-shop"
            >
              View in the Shop
            </motion.button>
          </div>
          {artist && (
            <p className="text-xs text-white/40 mt-10">
              Zebra One Gallery is an authorised dealer of the Estate of Shirley Baker.
            </p>
          )}
        </div>
      </Section>

      <Footer />
    </div>
  );
};

export default ShirleyBakerPage;
